import { Injectable, inject } from '@angular/core';
import { Storage, getDownloadURL, ref } from '@angular/fire/storage';
import { DomSanitizer } from '@angular/platform-browser';
import { ContentRef, Edition } from './search.dto';

declare let Prism: any;

@Injectable({
  providedIn: 'root',
})
export class SearchProcessorService {
  private sanitizer = inject(DomSanitizer);
  private storage = inject(Storage);

  async processSearchResults(res: any, language: string): Promise<Edition[]> {
    const editions: Edition[] = [];
    const chunksInContent: string[] = [];
    let chunksMapping: Array<{
      chunkId: string;
      contentId: string;
      contentRef?: ContentRef;
    }> = [];

    if (!res.contents) return [];

    res.contents.forEach((content: any) =>
      content.contents.forEach((e: any) => {
        chunksInContent.push(e.chunk);
        chunksMapping.push({
          chunkId: e.chunk,
          contentId: content._id,
        });
      })
    );

    chunksMapping = chunksMapping.map((e) => {
      const matchingCourses = res.matchingCourses || [];
      const note = res.matchingNotes ? res.matchingNotes.find((note: any) => note.content_id == e.contentId) : null;
      let matchingNote: any = { noteId: undefined };
      if (note) {
        matchingNote = { noteId: note._id };
      }

      const setMatchingBlogsOrNotes = () => {
        e.contentRef = { ...matchingNote, type: 'note' };
      };

      if (matchingCourses.length) {
        const courseWithContentID = matchingCourses.find((course: any) => course.contentIds.includes(e.contentId));
        if (courseWithContentID) {
          const contentRef = courseWithContentID.contentRefs;
          if (contentRef) {
            const matchingCourse = contentRef.find((contentRef: any) => contentRef.contentId == e.contentId);
            e.contentRef = { ...matchingCourse, type: 'course' };
          } else {
            setMatchingBlogsOrNotes();
          }
        } else {
          setMatchingBlogsOrNotes();
        }
      } else {
        setMatchingBlogsOrNotes();
      }
      return e;
    });

    const chunks = res.chunks || [];
    const downloadPromises: Promise<any>[] = [];

    for (const chunk of chunks.filter((chunk: any) => chunksInContent.includes(chunk?._id))) {
      const edition: Edition = chunk.editions.find((edition: any) => edition.language == language) || chunk.editions[0];
      const tags = chunk.tags;

      if (tags) {
        tags.forEach((tag: any) => {
          tag.display = tag.timestamp ? `${tag.content} (${tag.timestamp})` : tag.content;
        });
      }

      if (!edition) {
        continue;
      }
      edition.tags = tags;
      if (edition.type == 'html') {
        edition.sanitizedHTML = this.sanitizer.bypassSecurityTrustHtml(edition.content);
      }
      if (edition.type == 'code') {
        if (Prism && Prism.languages && Prism.languages[edition.codeLanguage]) {
          edition.content = Prism.highlight(edition.content, Prism.languages[edition.codeLanguage]);
        }
      }
      if (['video', 'image', 'file'].includes(edition.type)) {
        edition.waitngForDownloadUrl = true;
        const promise = this.getDownloadURL(edition.content)
          .then((url) => {
            edition.waitngForDownloadUrl = false;
            edition.content = url;
          })
          .catch((err) => {
            console.error('Error fetching download URL:', err);
            edition.waitngForDownloadUrl = false;
          });
        downloadPromises.push(promise);
      }
      const chunkMapping = chunksMapping.find((e) => e.chunkId == chunk?._id);
      if (chunkMapping && chunkMapping.contentRef) {
        edition.contentRef = chunkMapping.contentRef;
        edition.contentRef.contentId = chunkMapping.contentId;
      }

      if (edition.contentRef && (edition.contentRef.courseId || edition.contentRef.noteId)) {
        const e = edition.contentRef;
        if (edition.contentRef.courseId) {
          edition.routerLink = `knowledge/displayer/${e.courseId}/${e.sectionId}/${e.lectureId}`;
        } else {
          edition.routerLink = `notes/${e.noteId}`;
        }
        editions.push(edition);
      }
    }

    if (downloadPromises.length > 0) {
      await Promise.all(downloadPromises);
    }

    return editions;
  }

  private getDownloadURL(url: string) {
    const reference = ref(this.storage, url);
    return getDownloadURL(reference);
  }
}
